/** SUNTRONIX ONE — India Courier Partner Master (Phase 1).
 *
 *  Authoritative directory of courier / logistics partners used across
 *  Shipments, Reverse Pickups, Deployments, Warehouse dispatch,
 *  Costing and Contract Billing.  Replaces free-text carrier fields.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Truck, Plus, RefreshCcw, Power, PowerOff, X, Pencil, Globe2,
  MapPin, Search,
} from "lucide-react";

const STATUS_TONE = {
  ACTIVE:      "bg-emerald-100 text-emerald-800",
  INACTIVE:    "bg-amber-100 text-amber-900",
  DEACTIVATED: "bg-slate-200 text-slate-600 line-through",
};

const TYPE_LABEL = {
  national:      "National",
  regional:      "Regional",
  postal:        "Postal",
  international: "International",
  hyperlocal:    "Hyperlocal",
  third_party:   "3PL",
};

const TYPE_TONE = {
  national:      "bg-slate-100 text-slate-700",
  regional:      "bg-slate-100 text-slate-700",
  postal:        "bg-blue-100 text-blue-800",
  international: "bg-indigo-100 text-indigo-800",
  hyperlocal:    "bg-fuchsia-100 text-fuchsia-800",
  third_party:   "bg-teal-100 text-teal-800",
};

const MODE_LABEL = {
  surface:    "Surface",
  air:        "Air",
  express:    "Express",
  same_day:   "Same-day",
  hyperlocal: "Hyperlocal",
};

const EMPTY_FORM = {
  name: "", brand_short: "", code: "", type: "national",
  website: "", tracking_url_template: "",
  contact_phone: "", contact_email: "",
  hq_city: "", coverage_regions: "PAN_INDIA",
  serviceable_modes: [],
  supports_cod: false, supports_reverse: true,
  default_sla_hours: "", notes: "",
  sort_order: 100,
};


export default function CourierMaster() {
  const { user } = useAuth();
  const canManage = !user?.master_company_id;   // Suntronix admins only
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      if (typeFilter !== "all") params.type = typeFilter;
      const { data } = await api.get("/courier-partners", { params });
      setRows(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load couriers");
    } finally { setBusy(false); }
  }, [q, statusFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((r) => r.status === "ACTIVE").length;
    const intl = rows.filter((r) => r.type === "international").length;
    const hyper = rows.filter((r) => r.type === "hyperlocal").length;
    return { total, active, intl, hyper };
  }, [rows]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || "",
      brand_short: row.brand_short || "",
      code: row.code || "",
      type: row.type || "national",
      website: row.website || "",
      tracking_url_template: row.tracking_url_template || "",
      contact_phone: row.contact_phone || "",
      contact_email: row.contact_email || "",
      hq_city: row.hq_city || "",
      coverage_regions: (row.coverage_regions || []).join(", "),
      serviceable_modes: row.serviceable_modes || [],
      supports_cod: !!row.supports_cod,
      supports_reverse: row.supports_reverse !== false,
      default_sla_hours: row.default_sla_hours ?? "",
      notes: row.notes || "",
      sort_order: row.sort_order ?? 100,
    });
    setShowForm(true);
  };

  const toggleMode = (mode) => {
    setForm((f) => {
      const set = new Set(f.serviceable_modes || []);
      if (set.has(mode)) set.delete(mode); else set.add(mode);
      return { ...f, serviceable_modes: Array.from(set) };
    });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    const payload = {
      name: form.name.trim(),
      brand_short: form.brand_short.trim() || null,
      type: form.type,
      website: form.website.trim() || null,
      tracking_url_template: form.tracking_url_template.trim() || null,
      contact_phone: form.contact_phone.trim() || null,
      contact_email: form.contact_email.trim() || null,
      hq_city: form.hq_city.trim() || null,
      coverage_regions: form.coverage_regions
        .split(",").map((s) => s.trim()).filter(Boolean),
      serviceable_modes: form.serviceable_modes,
      supports_cod: !!form.supports_cod,
      supports_reverse: !!form.supports_reverse,
      default_sla_hours: form.default_sla_hours === "" ? null : Number(form.default_sla_hours),
      notes: form.notes.trim() || null,
      sort_order: Number(form.sort_order) || 100,
    };
    try {
      if (editing) {
        await api.put(`/courier-partners/${editing.id}`, payload);
        toast.success("Courier partner updated");
      } else {
        await api.post("/courier-partners", { ...payload, code: form.code.trim() || undefined });
        toast.success("Courier partner added");
      }
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const setStatus = async (row, next) => {
    if (row.status === next) return;
    try {
      await api.post(`/courier-partners/${row.id}/status`, { status: next });
      toast.success(`${row.name} → ${next}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status change failed");
    }
  };

  return (
    <div className="space-y-6 max-w-[1600px]" data-testid="courier-master">
      {/* ---------- Header ---------- */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
            India Logistics
          </div>
          <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1 flex items-center gap-2">
            <Truck className="h-6 w-6 text-slate-700" strokeWidth={1.6} />
            Courier Partner Master
          </h1>
          <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
            Authoritative directory of couriers used across Shipments, Reverse Pickups,
            Warehouse dispatch and Costing. Replaces free-text carrier fields.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={busy}
            data-testid="courier-refresh"
            className="h-9">
            <RefreshCcw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} strokeWidth={1.6} />
            Refresh
          </Button>
          {canManage && (
            <Button variant="create" onClick={openCreate}
              data-testid="courier-add"
              className="h-9 bg-slate-900 hover:bg-slate-800">
              <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.8} />
              Add courier
            </Button>
          )}
        </div>
      </div>

      {/* ---------- KPIs ---------- */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Partners" value={summary.total} testId="kpi-total" />
        <Kpi label="Active" value={summary.active} tone="emerald" testId="kpi-active" />
        <Kpi label="International" value={summary.intl} tone="indigo" testId="kpi-intl" />
        <Kpi label="Hyperlocal" value={summary.hyper} tone="fuchsia" testId="kpi-hyper" />
      </div>

      {/* ---------- Filters ---------- */}
      <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" strokeWidth={1.6} />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or code…"
            data-testid="courier-search"
            className="h-9 pl-8" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-40" data-testid="courier-filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="INACTIVE">Inactive</SelectItem>
            <SelectItem value="DEACTIVATED">Deactivated</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-9 w-44" data-testid="courier-filter-type">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {Object.entries(TYPE_LABEL).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* ---------- List ---------- */}
      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {busy && rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            No courier partners match your filters.
          </div>
        ) : rows.map((r) => (
          <CourierRow key={r.id} row={r}
            canManage={canManage}
            onEdit={() => openEdit(r)}
            onSetStatus={(next) => setStatus(r, next)} />
        ))}
      </div>

      {/* ---------- Form dialog ---------- */}
      <Dialog open={showForm} onOpenChange={(v) => { if (!v) { setShowForm(false); setEditing(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="courier-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit courier partner" : "Add courier partner"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <Field label="Name *">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="courier-input-name" />
            </Field>
            <Field label="Short brand">
              <Input value={form.brand_short}
                onChange={(e) => setForm({ ...form, brand_short: e.target.value })}
                data-testid="courier-input-brand" />
            </Field>
            {!editing && (
              <Field label="Code (auto if blank)">
                <Input value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  data-testid="courier-input-code" />
              </Field>
            )}
            <Field label="Type">
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="h-9" data-testid="courier-input-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="HQ city">
              <Input value={form.hq_city}
                onChange={(e) => setForm({ ...form, hq_city: e.target.value })} />
            </Field>
            <Field label="Coverage (comma-separated)">
              <Input value={form.coverage_regions}
                onChange={(e) => setForm({ ...form, coverage_regions: e.target.value })}
                placeholder="PAN_INDIA or KA, TN, MH" />
            </Field>
            <Field label="Website" wide>
              <Input value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })} />
            </Field>
            <Field label="Tracking URL template" wide>
              <Input value={form.tracking_url_template}
                onChange={(e) => setForm({ ...form, tracking_url_template: e.target.value })}
                placeholder="https://carrier.example/track?awb={awb}" />
            </Field>
            <Field label="Contact phone">
              <Input value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
            </Field>
            <Field label="Contact email">
              <Input value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
            </Field>
            <Field label="Default SLA (hours)">
              <Input type="number" value={form.default_sla_hours}
                onChange={(e) => setForm({ ...form, default_sla_hours: e.target.value })} />
            </Field>
            <Field label="Sort order">
              <Input type="number" value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
            </Field>
            <div className="col-span-2">
              <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1.5">Serviceable modes</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(MODE_LABEL).map(([k, v]) => {
                  const on = (form.serviceable_modes || []).includes(k);
                  return (
                    <button key={k} type="button"
                      onClick={() => toggleMode(k)}
                      data-testid={`courier-mode-${k}`}
                      className={
                        "h-7 px-2.5 rounded-full text-[12px] border transition-colors " +
                        (on
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")
                      }>
                      {v}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="col-span-2 flex flex-wrap gap-4 pt-1">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.supports_cod}
                  onChange={(e) => setForm({ ...form, supports_cod: e.target.checked })}
                  data-testid="courier-input-cod" />
                Supports COD
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.supports_reverse}
                  onChange={(e) => setForm({ ...form, supports_reverse: e.target.checked })}
                  data-testid="courier-input-reverse" />
                Supports reverse pickup
              </label>
            </div>
            <Field label="Notes" wide>
              <Input value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditing(null); }}
              data-testid="courier-form-cancel">
              Cancel
            </Button>
            <Button onClick={save} className="bg-slate-900 text-white hover:bg-slate-800"
              data-testid="courier-form-save">
              {editing ? "Save changes" : "Add courier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function Kpi({ label, value, tone = "slate", testId }) {
  const toneMap = {
    slate:    "text-slate-900",
    emerald:  "text-emerald-700",
    indigo:   "text-indigo-700",
    fuchsia:  "text-fuchsia-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3.5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className={"text-2xl font-heading font-semibold mt-1 " + (toneMap[tone] || toneMap.slate)}>
        {value}
      </div>
    </div>
  );
}


function Field({ label, children, wide }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}


function CourierRow({ row, canManage, onEdit, onSetStatus }) {
  const coverage = (row.coverage_regions || []).join(", ") || "—";
  const modes = (row.serviceable_modes || []).map((m) => MODE_LABEL[m] || m).join(" · ") || "—";
  return (
    <div className="p-4 flex flex-wrap items-start gap-4 hover:bg-slate-50/50"
      data-testid={`courier-row-${row.code || row.id}`}>
      <div className="h-9 w-9 rounded-md bg-slate-900 text-white grid place-items-center shrink-0">
        <Truck className="h-4 w-4" strokeWidth={1.6} />
      </div>
      <div className="flex-1 min-w-[260px]">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[14px] font-semibold text-slate-900">{row.name}</div>
          <span className={"h-5 px-2 rounded-full text-[10px] font-medium uppercase tracking-[0.1em] " + STATUS_TONE[row.status]}
            data-testid={`courier-status-${row.code}`}>
            {row.status}
          </span>
          <span className={"h-5 px-2 rounded-full text-[10px] font-medium " + (TYPE_TONE[row.type] || "bg-slate-100 text-slate-700")}>
            {TYPE_LABEL[row.type] || row.type}
          </span>
          <span className="text-[11px] text-slate-500 font-mono">{row.code}</span>
        </div>
        <div className="text-[12px] text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" strokeWidth={1.6} />
            {row.hq_city || "—"}
          </span>
          <span className="inline-flex items-center gap-1">
            <Globe2 className="h-3 w-3" strokeWidth={1.6} />
            {coverage}
          </span>
          <span>Modes: {modes}</span>
          {row.supports_cod && <span className="text-emerald-700">COD</span>}
          {row.supports_reverse && <span className="text-indigo-700">Reverse ✓</span>}
        </div>
        {row.tracking_url_template && (
          <div className="text-[11px] text-slate-400 font-mono mt-1 truncate">
            {row.tracking_url_template}
          </div>
        )}
      </div>
      {canManage && (
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" onClick={onEdit} className="h-8"
            data-testid={`courier-edit-${row.code}`}>
            <Pencil className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.6} /> Edit
          </Button>
          {row.status === "ACTIVE" ? (
            <Button variant="outline" size="sm" onClick={() => onSetStatus("INACTIVE")}
              className="h-8 text-amber-700 hover:text-amber-800"
              data-testid={`courier-pause-${row.code}`}>
              <PowerOff className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.6} /> Pause
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => onSetStatus("ACTIVE")}
              className="h-8 text-emerald-700 hover:text-emerald-800"
              data-testid={`courier-activate-${row.code}`}>
              <Power className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.6} /> Activate
            </Button>
          )}
          {row.status !== "DEACTIVATED" && (
            <Button variant="ghost" size="sm" onClick={() => onSetStatus("DEACTIVATED")}
              className="h-8 text-slate-500 hover:text-red-700"
              data-testid={`courier-deactivate-${row.code}`}>
              <X className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.6} /> Retire
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
